import { Button } from '@/components/ui/Button'
import { Box, Grid, HStack, Text, VStack } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

/**
 * Buyer Detail Card Component
 * Displays buyer information in a card format with edit/remove actions
 */
export function BuyerCard({ buyer, onEdit, onRemove }) {
  const { t } = useTranslation()

  if (!buyer) return null

  return (
    <Box
      p={ 4 }
      border="1px solid"
      borderColor="#DFE1E6"
      borderRadius="sm"
      bg="white"
    >
      <VStack spacing={ 3 } align="stretch">
        {/* Name and Actions */}
        <HStack justify="space-between">
          <Text fontSize="md" fontWeight="600" color="#172B4D">
            {buyer.client_name || buyer.name || t('buyers.unnamed')}
          </Text>
          <HStack spacing={ 2 }>
            <Button
              size="sm"
              variant="outline"
              onClick={onEdit}
            >
              {t('common.edit')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              color="#DE350B"
              onClick={onRemove}
              _hover={{ bg: '#FFEBE6' }}
            >
              {t('common.remove')}
            </Button>
          </HStack>
        </HStack>

        {/* Contact Information */}
        <Grid templateColumns="repeat(2, 1fr)" gap={ 3 }>
          {(buyer.client_phone || buyer.phone_first) && (
            <Box>
              <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
                {t('buyers.phoneFirst')}
              </Text>
              <Text fontSize="sm" color="#172B4D">
                {buyer.client_phone || buyer.phone_first}
              </Text>
            </Box>
          )}
          {buyer.phone_second && (
            <Box>
              <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
                {t('buyers.phoneSecond')}
              </Text>
              <Text fontSize="sm" color="#172B4D">
                {buyer.phone_second}
              </Text>
            </Box>
          )}
          {buyer.email && (
            <Box>
              <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
                {t('buyers.email')}
              </Text>
              <Text fontSize="sm" color="#172B4D">
                {buyer.email}
              </Text>
            </Box>
          )}
          {buyer.commission && (
            <Box>
              <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
                {t('buyers.commission')}
              </Text>
              <Text fontSize="sm" color="#172B4D">
                {buyer.commission}
              </Text>
            </Box>
          )}
        </Grid>

        {/* Price Range */}
        {(buyer.price_from || buyer.price_to) && (
          <Box>
            <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
              {t('buyers.priceRange')}
            </Text>
            <Text fontSize="sm" color="#172B4D">
              {buyer.price_from ? `$${buyer.price_from.toLocaleString()}` : '—'}
              {' - '}
              {buyer.price_to ? `$${buyer.price_to.toLocaleString()}` : '—'}
            </Text>
          </Box>
        )}

        {/* Rooms Range */}
        {(buyer.rooms_from || buyer.rooms_to) && (
          <Box>
            <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
              {t('buyers.roomsRange')}
            </Text>
            <Text fontSize="sm" color="#172B4D">
              {buyer.rooms_from || '—'} - {buyer.rooms_to || '—'}
            </Text>
          </Box>
        )}

        {/* Area Range */}
        {(buyer.area_from || buyer.area_to) && (
          <Box>
            <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
              {t('buyers.areaRange')}
            </Text>
            <Text fontSize="sm" color="#172B4D">
              {buyer.area_from ? `${buyer.area_from} м²` : '—'}
              {' - '}
              {buyer.area_to ? `${buyer.area_to} м²` : '—'}
            </Text>
          </Box>
        )}

        {/* Comment */}
        {buyer.comment && (
          <Box>
            <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
              {t('buyers.comment')}
            </Text>
            <Text fontSize="sm" color="#172B4D">
              {buyer.comment}
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  )
}

export default BuyerCard
